import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { ShoppingBagIcon, TruckIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon, StarIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ReviewForm from '../components/reviews/ReviewForm';

const Orders = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [reviewingProduct, setReviewingProduct] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const toggleOrder = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const { data: ordersData, isLoading } = useQuery(
    ['my-orders', statusFilter],
    () => api.get('/orders/my-orders', {
      params: { status: statusFilter }
    }).then(res => res.data.data)
  );

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'معلق' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'قيد المعالجة' },
      shipped: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'مشحون' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', label: 'مسلم' },
      completed: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'مكتمل' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'ملغي' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const handleReviewProduct = (product, orderId) => {
    setReviewingProduct({ ...product, orderId });
    setShowReviewForm(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="w-6 h-6 text-green-600" />;
      case 'cancelled':
        return <XCircleIcon className="w-6 h-6 text-red-600" />;
      case 'shipped':
        return <TruckIcon className="w-6 h-6 text-purple-600" />;
      default:
        return <ShoppingBagIcon className="w-6 h-6 text-blue-600" />;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // ordersData is the paginated result, so we need to access .data property
  const orders = ordersData?.data || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">طلباتي</h1>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        >
          <option value="">جميع الطلبات</option>
          <option value="pending">معلق</option>
          <option value="processing">قيد المعالجة</option>
          <option value="shipped">مشحون</option>
          <option value="delivered">مسلم</option>
          <option value="completed">مكتمل</option>
          <option value="cancelled">ملغي</option>
        </select>
      </div>

      {/* Orders List */}
      {orders.length > 0 ? (
        <div className="space-y-4">
          {orders.map((order) => {
            const isExpanded = expandedOrders.has(order.id);
            return (
              <div key={order.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Order Header - Clickable */}
                <div 
                  onClick={() => toggleOrder(order.id)}
                  className="bg-gray-50 px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(order.status)}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          طلب #{order.order_number}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {new Date(order.created_at).toLocaleDateString('ar-SA', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {order.items?.length || 0} منتج
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-left">
                        {getStatusBadge(order.status)}
                        <p className="text-lg font-bold text-gray-900 mt-2">
                          {parseFloat(order.total_amount).toLocaleString()} جنيه
                        </p>
                      </div>
                      {isExpanded ? (
                        <ChevronUpIcon className="w-6 h-6 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Order Details - Collapsible */}
                {isExpanded && (
                  <div className="p-6 border-t border-gray-200">
                <div className="space-y-4">
                  {order.items?.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 pb-4 border-b last:border-b-0">
                      {item.product?.images?.[0]?.url && (
                        <img
                          src={item.product.images[0].url}
                          alt={item.product.name}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{item.product?.name || 'منتج'}</h4>
                        <p className="text-sm text-gray-600">
                          الكمية: {item.quantity} × {parseFloat(item.price).toLocaleString()} جنيه
                        </p>
                        {item.vendor && (
                          <p className="text-sm text-gray-500">
                            البائع: {item.vendor.store_name}
                          </p>
                        )}
                      </div>
                      <div className="text-left space-y-2">
                        <p className="font-semibold text-gray-900">
                          {parseFloat(item.total).toLocaleString()} جنيه
                        </p>
                        {getStatusBadge(item.status)}
                        {/* Review Button - Show only for completed orders */}
                        {order.status === 'completed' && item.product && (
                          <button
                            onClick={() => handleReviewProduct(item.product, order.id)}
                            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                          >
                            <StarIcon className="w-4 h-4" />
                            تقييم المنتج
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                  {/* Order Summary */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Shipping Address */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">عنوان الشحن</h4>
                      <p className="text-sm text-gray-600">
                        {typeof order.shipping_address === 'string' 
                          ? order.shipping_address 
                          : order.shipping_address?.address || JSON.stringify(order.shipping_address)}
                      </p>
                      {order.phone && (
                        <p className="text-sm text-gray-600 mt-1">الهاتف: {order.phone}</p>
                      )}
                    </div>

                    {/* Payment Info */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">معلومات الدفع</h4>
                      <p className="text-sm text-gray-600">
                        طريقة الدفع: {order.payment_method === 'cash_on_delivery' ? 'الدفع عند الاستلام' : 'تحويل بنكي'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        حالة الدفع: {order.payment_status === 'paid' ? 'مدفوع' : 'غير مدفوع'}
                      </p>
                    </div>
                  </div>

                  {/* Price Breakdown */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">المجموع الفرعي</span>
                        <span className="font-medium">{parseFloat(order.subtotal || 0).toLocaleString()} جنيه</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">الشحن</span>
                        <span className="font-medium">{parseFloat(order.shipping_cost || 0).toLocaleString()} جنيه</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">الضريبة</span>
                        <span className="font-medium">{parseFloat(order.tax_amount || 0).toLocaleString()} جنيه</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>الإجمالي</span>
                        <span className="text-primary-600">{parseFloat(order.total_amount || 0).toLocaleString()} جنيه</span>
                      </div>
                    </div>
                  </div>

                  {order.notes && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-2">ملاحظات</h4>
                      <p className="text-sm text-gray-600">{order.notes}</p>
                    </div>
                  )}
                </div>
              </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ShoppingBagIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">لا توجد طلبات</h2>
          <p className="text-gray-600 mb-6">لم تقم بإنشاء أي طلبات بعد</p>
          <a
            href="/products"
            className="inline-block px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            تصفح المنتجات
          </a>
        </div>
      )}

      {/* Review Form Modal */}
      {reviewingProduct && (
        <ReviewForm
          isOpen={showReviewForm}
          onClose={() => {
            setShowReviewForm(false);
            setReviewingProduct(null);
          }}
          type="product"
          productId={reviewingProduct.id}
          vendorName={reviewingProduct.name}
        />
      )}
    </div>
  );
};

export default Orders;
